import debounce from "lodash/debounce"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "selectedItems", "selectedUserTemplate", "submitButton"]
  static values = {
    searchUrl: String
  }

  // Lifecycle hooks
  connect() {
    this.selectedUsers = new Set()
    window.addEventListener("click", this.onWindowClick);
    window.addEventListener("keydown", this.onEscapeKeydown);
    window.addEventListener("keyup", this.onKeyup);
  }

  disconnect() {
    window.removeEventListener("click", this.onWindowClick);
    window.removeEventListener("keydown", this.onEscapeKeydown);
    window.removeEventListener("keyup", this.onKeyup);
  }

  // Functions
  search() {
    const searchTerm = this.inputTarget.value;
    // if the search term is empty or less than 3 characters, return
    if (searchTerm.length < 3) {
      this.hideOptions();
      return;
    }

    const searchUrl = new URL(this.searchUrlValue, window.location.origin);
    searchUrl.searchParams.set("term", searchTerm);

    // make a request to the search url with the search term
    fetch(searchUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        // filter out any users that are already selected
        const filteredData = data.filter(user => !this.selectedUsers.has(user.uuid));
        return filteredData;
      })
      .then(data => {
        this.resultsTarget.innerHTML = data.map(user => {
          const userJson = JSON.stringify(user).replace(/"/g, '&quot;');
          return `<li class="option" data-action="click->multi-select#onResultClick" data-multi-select-user-item="${userJson}">${user.display_name} (${user.email})</li>`;
        }).join('');
        this.showOptions();
      })
      .catch(error => {
        console.error("Error searching for users:", error);
      });
  }

  showSearchResults() {
    if (this.inputTarget.value.length > 0) {
      if (!this.hasResultsTarget) return;
      this.resultsTarget.classList.remove(this.hiddenClass);
    };
  }

  updateSubmitButton() {
    if (this.selectedUsers.size > 0) {
      this.submitButtonTarget.disabled = false;
    } else {
      this.submitButtonTarget.disabled = true;
    }
  }

  clearSelection() {
    this.results.forEach(item => {
      item.classList.remove('selected');
    });
  }

  clearInput() {
    this.inputTarget.value = '';
  }

  selectResult(result) {
    this.clearSelection();
    result.remove();
    const userData = JSON.parse(result.dataset.multiSelectUserItem);

    if (!this.selectedUsers.has(userData.uuid)) {
      this.selectedUsers.add(userData.uuid);
      this.addSelectedUser(userData);
    }

    this.inputTarget.value = '';

    this.hideOptions();
  }

  addSelectedUser(userData) {
    const template = this.selectedUserTemplateTarget.content.cloneNode(true);

    // Set the user data in the template
    template.querySelector('.selected-user__name').textContent = userData.display_name;
    template.querySelector('.selected-user__email').textContent = `(${userData.email})`;
    template.querySelector('input[name="user_ids[]"]').value = userData.uuid;

    this.selectedItemsTarget.appendChild(template);
    this.updateSubmitButton();
  }

  removeUser(event) {
    const selectedUser = event.target.closest('.selected-user');
    const userId = selectedUser.querySelector('input[name="user_ids[]"]').value;

    this.selectedUsers.delete(userId);
    selectedUser.remove();
    this.updateSubmitButton();
  }

  navigate(indexDiff = 1) {
    if (!this.hasResultsTarget) return;
    const selectedIndex = this.selectedResultIndex;
    const resultLength = this.results.length;
    console.log("selectedIndex", selectedIndex)
    console.log("resultLength", resultLength)
    const newIndex = selectedIndex === -1 ? 0 : selectedIndex + indexDiff;

    this.clearSelection();

    // Handle out of bounds by setting the focus back on the search bar
    if ((selectedIndex === resultLength - 1 && indexDiff === 1) || (selectedIndex === 0 && indexDiff === -1)) {
      this.inputTarget.focus();
      return;
    }

    this.results[newIndex].classList.add('selected');
  }

  chooseSelectedResult() {
    const selectedResult = this.results[this.selectedResultIndex]
    if (selectedResult == null) return;

    this.selectResult(selectedResult);
    this.hideOptions();
  }

  // Getters
  get results() {
    if (!this.hasResultsTarget) return [];
    let items = this.resultsTarget.getElementsByClassName("option");
    return Array.from(items);
  }

  get selectedResultIndex() {
    return this.results.findIndex(item => item.classList.contains('selected'));
  }

  // Actions
  submit = debounce(() => {
    this.search();
  }, 400);

  focus() {
    if (this.results.length > 0) {
      this.showSearchResults();
    }
  }

  // Event handlers
  onResultClick(event) {
    event.preventDefault();
    this.selectResult(event.currentTarget);
  }

  onWindowClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.hideOptions();
    }
  }

  onKeyup = (event) => {
    switch (event.key) {
      case "Up": // support Edge/IE
      case "ArrowUp":
        this.navigate(-1);
        event.preventDefault();
        break;
      case "Down": // support Edge/IE
      case "ArrowDown":
        this.navigate(1);
        event.preventDefault();
        break;
      case "Enter":
        this.chooseSelectedResult();
        break;
      case "Esc": // support Edge/IE
      case "Escape":
        this.clearInput();
        this.hideOptions();
      default:
        return;
    }
  }

  showOptions() {
    this.resultsTarget.classList.remove("hidden")
  }

  hideOptions() {
    this.resultsTarget.classList.add("hidden")
  }
}
